import { secondAxios } from "../../../../../../api/axios";

/**
 * MCS APIs used for Schedule4D (per MCS_API_BRIEF.md).
 * Base URL is relative to secondAxios (e.g. /api).
 */

/** Tagged materials for plant – main list for schedule */
export const getTaggedMaterials = async (plantId) => {
  const { data } = await secondAxios.get(`/material/tagged/${plantId}`);
  return data;
};

/** MRQs for plant (with mrqMaterials) */
export const getMrqsForPlant = async (plantId) => {
  const { data } = await secondAxios.get(`/mrq/plant/${plantId}`);
  return data;
};

/** POs for an MRQ (with poMaterials) */
export const getPosForMrq = async (mrqId) => {
  const { data } = await secondAxios.get(`/po/mrq/${mrqId}`);
  return data;
};

/** All material records (estCost, remainingQty, mrqMaterialsIds) */
export const getMaterialRecords = async () => {
  const { data } = await secondAxios.get(`/material-record`);
  return data;
};

/** List all MRVs */
export const getMrvList = async () => {
  const { data } = await secondAxios.get(`/mrv`);
  return data;
};

/** Single MRV with materials (mrvMaterials, mrvMrqMaterials) */
export const getMrvById = async (id) => {
  const { data } = await secondAxios.get(`/mrv/${id}`);
  return data;
};

/**
 * Extract WBS code from mrqMaterial.wbs (e.g. "2.1 (Procure...)" -> "2.1").
 */
function extractWbsCode(wbs) {
  if (wbs == null || wbs === "") return "";
  const str = String(wbs).trim();
  return str.split(/\s*\(/)[0].trim() || str;
}

/**
 * Task lookup keys for an MRQ line: use line wbs when set, else MRQ header wbs
 * (API often sends mrqMaterials[].wbs null while Mrq.wbs holds "2.2.1-MRQ-Struct").
 * Returns full code, segment before first "-", and parenthetical extract when distinct.
 */
function wbsLookupKeys(lineWbs, headerWbs) {
  const line = lineWbs != null ? String(lineWbs).trim() : "";
  const header = headerWbs != null ? String(headerWbs).trim() : "";
  const source = line || header;
  if (!source || source.toLowerCase() === "procurement") return [];
  const keys = new Set([source]);
  const dashIdx = source.indexOf("-");
  if (dashIdx > 0) keys.add(source.slice(0, dashIdx).trim());
  const paren = extractWbsCode(source);
  if (paren && paren !== source) keys.add(paren);
  return [...keys].filter((c) => c && c.toLowerCase() !== "procurement");
}

/**
 * When MRQ has no mrqMaterials, still attach header fields (wbs, mrqNumber) to tasks
 * keyed by WBS string, numeric WBS prefix before "-", and optional taskId.
 */
function attachMrqHeaderOnly(mrq, byTaskCode, byTaskId) {
  const synthetic = {
    id: `__mrq-header-${mrq.id}`,
    wbs: mrq.wbs,
    mrq,
    poMaterials: [],
    poId: undefined,
  };

  const pushUnique = (entry) => {
    if (!entry || entry.mrqMaterials.some((m) => m.mrq?.id === mrq.id)) return;
    entry.mrqMaterials.push(synthetic);
  };

  const wbsFull = mrq.wbs != null ? String(mrq.wbs).trim() : "";
  if (wbsFull) {
    const keys = new Set([wbsFull]);
    const dashIdx = wbsFull.indexOf("-");
    if (dashIdx > 0) keys.add(wbsFull.slice(0, dashIdx).trim());
    const parenCode = extractWbsCode(mrq.wbs);
    if (parenCode && parenCode !== wbsFull) keys.add(parenCode);
    for (const code of keys) {
      if (!code || code.toLowerCase() === "procurement") continue;
      if (!byTaskCode.has(code)) byTaskCode.set(code, { mrqMaterials: [], mrvMaterials: [] });
      pushUnique(byTaskCode.get(code));
    }
  }

  if (mrq.taskId) {
    const tid = String(mrq.taskId);
    if (!byTaskId.has(tid)) byTaskId.set(tid, { mrqMaterials: [], mrvMaterials: [] });
    pushUnique(byTaskId.get(tid));
  }
}

/**
 * Fetches MRQ/PO/MRV data for status lookup. Returns
 * { byTaskCode: Map<taskCode, { mrqMaterials, mrvMaterials }>, byTaskId: Map<taskId, ...> }.
 * Legacy: callers may still receive a plain Map from older code paths — use enrichTasksWithMcsStatus which accepts both.
 */
export const getMcsStatusForTasks = async (plantId) => {
  if (!plantId) return { byTaskCode: new Map(), byTaskId: new Map() };
  const [mrqs, mrvList] = await Promise.all([
    getMrqsForPlant(plantId).catch(() => []),
    getMrvList().catch(() => []),
  ]);

  console.log("[Schedule4D MRV] GET /mrv (list)", {
    plantId,
    count: Array.isArray(mrvList) ? mrvList.length : 0,
    mrvList,
  });

  const mrqIds = (mrqs || []).map((m) => m.id).filter(Boolean);
  const poArrays = await Promise.all(
    mrqIds.map((id) => getPosForMrq(id).catch(() => []))
  );
  const posByMrqId = {};
  mrqIds.forEach((id, i) => {
    posByMrqId[id] = Array.isArray(poArrays[i]) ? poArrays[i] : [];
  });

  const toKey = (id) => (id == null ? null : String(id));
  const byTaskCode = new Map();
  const byTaskId = new Map();
  /** MRQ line id -> WBS keys + MRQ task id (MRV detail often omits wbs on nested mrqMaterial) */
  const mrqMaterialScheduleIndex = new Map();

  (mrqs || []).forEach((mrq) => {
    const pos = posByMrqId[mrq.id] || [];
    const poMaterialsByMrqMaterialId = new Map();
    pos.forEach((po) => {
      (po.poMaterials || []).forEach((pm) => {
        const key = toKey(pm.mrqMaterialId ?? pm.mrqMaterial?.id);
        if (!key) return;
        if (!poMaterialsByMrqMaterialId.has(key)) poMaterialsByMrqMaterialId.set(key, []);
        poMaterialsByMrqMaterialId.get(key).push({ ...pm, po });
      });
    });

    (mrq.mrqMaterials || []).forEach((mm) => {
      const keys = wbsLookupKeys(mm.wbs, mrq.wbs);
      if (mm.id != null) {
        mrqMaterialScheduleIndex.set(toKey(mm.id), {
          keys,
          mrqTaskId: mrq.taskId != null ? String(mrq.taskId) : null,
        });
      }
      const enriched = {
        ...mm,
        mrq,
        poMaterials: poMaterialsByMrqMaterialId.get(toKey(mm.id)) || [],
        poId: (poMaterialsByMrqMaterialId.get(toKey(mm.id)) || [])[0]?.po?.id ?? mm.poId,
      };

      const pushToTaskCode = (code) => {
        if (!code || code.toLowerCase() === "procurement") return;
        if (!byTaskCode.has(code)) byTaskCode.set(code, { mrqMaterials: [], mrvMaterials: [] });
        const entry = byTaskCode.get(code);
        if (!entry.mrqMaterials.some((m) => m.id === enriched.id)) {
          entry.mrqMaterials.push(enriched);
        }
      };

      keys.forEach(pushToTaskCode);

      if (mrq.taskId) {
        const tid = String(mrq.taskId);
        if (!byTaskId.has(tid)) byTaskId.set(tid, { mrqMaterials: [], mrvMaterials: [] });
        const e = byTaskId.get(tid);
        if (!e.mrqMaterials.some((m) => m.id === enriched.id)) e.mrqMaterials.push(enriched);
      }
    });

    if (!(mrq.mrqMaterials || []).length) {
      attachMrqHeaderOnly(mrq, byTaskCode, byTaskId);
    }
  });

  const mrvDetails = await Promise.all(
    (mrvList || []).map((m) => getMrvById(m.id).catch(() => null))
  );

  console.log("[Schedule4D MRV] GET /mrv/:id (details)", {
    plantId,
    count: mrvDetails.filter(Boolean).length,
    mrvDetails,
  });

  mrvDetails.forEach((mrv) => {
    if (!mrv) return;
    const mrvMrqRows = mrv.mrvMrqMaterials || mrv.mrv_mrq_materials || [];
    mrvMrqRows.forEach((mrm) => {
      const mm = mrm.mrqMaterial || mrm.mrq_material;
      const mrqMaterialId =
        mrm.mrqMaterialId ?? mrm.mrq_material_id ?? mm?.id;
      if (!mrqMaterialId) return;

      const fromMrq = mrqMaterialScheduleIndex.get(toKey(mrqMaterialId));
      let mrvKeys =
        fromMrq?.keys?.length
          ? fromMrq.keys
          : wbsLookupKeys(mm?.wbs, mm?.mrq?.wbs);
      const taskIdFallback =
        fromMrq?.mrqTaskId ||
        (mm?.mrq?.taskId != null ? String(mm.mrq.taskId) : null);

      if (!mrvKeys.length && taskIdFallback) {
        if (!byTaskId.has(taskIdFallback)) {
          byTaskId.set(taskIdFallback, { mrqMaterials: [], mrvMaterials: [] });
        }
        const entry = byTaskId.get(taskIdFallback);
        if (!entry.mrvMaterials.some((m) => m.id === mrv.id)) {
          entry.mrvMaterials.push({ id: mrv.id, ...mrv });
        }
        const mrqMatLinked = entry.mrqMaterials.find((m) => m.id === mrqMaterialId);
        if (mrqMatLinked) {
          mrqMatLinked.mrvMrqMaterials = mrqMatLinked.mrvMrqMaterials || [];
          if (!mrqMatLinked.mrvMrqMaterials.some((x) => x.mrv?.id === mrv.id)) {
            mrqMatLinked.mrvMrqMaterials.push({ mrv });
          }
        }
        return;
      }

      if (!mrvKeys.length) return;

      let mrqMatLinked = null;
      for (const code of mrvKeys) {
        if (!code || code.toLowerCase() === "procurement") continue;
        if (!byTaskCode.has(code)) byTaskCode.set(code, { mrqMaterials: [], mrvMaterials: [] });
        const entry = byTaskCode.get(code);
        const mat = entry.mrqMaterials.find((m) => m.id === mrqMaterialId);
        if (mat && !mrqMatLinked) mrqMatLinked = mat;
        if (!entry.mrvMaterials.some((m) => m.id === mrv.id)) {
          entry.mrvMaterials.push({ id: mrv.id, ...mrv });
        }
      }
      if (taskIdFallback && byTaskId.has(taskIdFallback)) {
        const entry = byTaskId.get(taskIdFallback);
        if (!entry.mrvMaterials.some((m) => m.id === mrv.id)) {
          entry.mrvMaterials.push({ id: mrv.id, ...mrv });
        }
        const mat = entry.mrqMaterials.find((m) => m.id === mrqMaterialId);
        if (mat && !mrqMatLinked) mrqMatLinked = mat;
      }
      if (mrqMatLinked) {
        mrqMatLinked.mrvMrqMaterials = mrqMatLinked.mrvMrqMaterials || [];
        if (!mrqMatLinked.mrvMrqMaterials.some((x) => x.mrv?.id === mrv.id)) {
          mrqMatLinked.mrvMrqMaterials.push({ mrv });
        }
      }
    });
  });

  return { byTaskCode, byTaskId };
};

/**
 * Fetches all data needed for Schedule4D: materials for plant and their
 * MRQ/PO/MRV status. Returns a flat list of materials with mrqMaterials
 * (and nested po, mrv) and mrvMaterials for column consumption.
 */
export const getSchedule4DData = async (plantId) => {
  if (!plantId) return [];

  const [
    taggedPayload,
    mrqs,
    materialRecordsPayload,
    mrvList,
  ] = await Promise.all([
    getTaggedMaterials(plantId).catch(() => null),
    getMrqsForPlant(plantId).catch(() => []),
    getMaterialRecords().catch(() => []),
    getMrvList().catch(() => []),
  ]);

  // Normalize tagged materials to array of material-like objects
  let materials = [];
  if (taggedPayload != null) {
    if (Array.isArray(taggedPayload)) {
      materials = taggedPayload.map((item) => {
        if (item.materials && Array.isArray(item.materials)) {
          return item.materials.map((m) => ({ ...m, drawing: item.drawing || item }));
        }
        if (item.material) return { ...item.material, taggedMaterial: item };
        return { ...item };
      }).flat();
    } else if (taggedPayload.materials && Array.isArray(taggedPayload.materials)) {
      materials = taggedPayload.materials;
    } else {
      materials = [];
    }
  }

  // Index each material by all possible ids so MRQ/PO lookup finds the same row
  const materialById = new Map();
  const registerMaterial = (m, base = {}) => {
    const obj = { ...base, ...m, mrqMaterials: m.mrqMaterials || [], mrvMaterials: m.mrvMaterials || [] };
    const ids = [
      m.id,
      m.materialId,
      m.taggedMaterial?.id,
      m.bulkMaterial?.id,
      m.material?.id,
      obj.id,
      obj.materialId,
    ].filter((id) => id != null && id !== "");
    ids.forEach((id) => materialById.set(String(id), obj));
    return obj;
  };
  materials.forEach((m) => {
    registerMaterial(m, { mrqMaterials: [], mrvMaterials: [] });
  });

  // Fetch POs for all MRQs in parallel
  const mrqIds = (mrqs || []).map((m) => m.id).filter(Boolean);
  const poArrays = await Promise.all(
    mrqIds.map((id) => getPosForMrq(id).catch(() => []))
  );
  const posByMrqId = {};
  mrqIds.forEach((id, i) => {
    posByMrqId[id] = Array.isArray(poArrays[i]) ? poArrays[i] : [];
  });

  // Helper to get a consistent key for PO -> mrqMaterial matching
  const toKey = (id) => (id == null ? null : String(id));

  // Attach mrqMaterials (and poMaterials) to each material
  (mrqs || []).forEach((mrq) => {
    const pos = posByMrqId[mrq.id] || [];
    const poMaterialsByMrqMaterialId = new Map();
    pos.forEach((po) => {
      (po.poMaterials || []).forEach((pm) => {
        const key = toKey(pm.mrqMaterialId ?? pm.mrqMaterial?.id);
        if (!key) return;
        if (!poMaterialsByMrqMaterialId.has(key)) {
          poMaterialsByMrqMaterialId.set(key, []);
        }
        poMaterialsByMrqMaterialId.get(key).push({
          ...pm,
          po: po,
          cost: pm.cost,
          time: pm.time,
        });
      });
    });

    (mrq.mrqMaterials || []).forEach((mm) => {
      const materialId =
        mm.materialId ??
        mm.material?.id ??
        mm.materials?.[0]?.id ??
        mm.materials?.[0]?.materialId;
      const mmId = mm.id;
      const lookupIds = [
        materialId,
        mm.material?.id,
        mm.materialId,
        mm.materials?.[0]?.id,
        mm.materials?.[0]?.materialId,
      ]
        .filter(Boolean)
        .map(toKey);
      let mat = null;
      for (const id of lookupIds) {
        mat = materialById.get(id);
        if (mat) break;
      }
      if (!mat) {
        const baseId = materialId || mm.material?.id || mmId;
        mat = registerMaterial(
          { ...(mm.material || mm.materials?.[0] || {}), id: baseId },
          { mrqMaterials: [], mrvMaterials: [] }
        );
      }
      const poMaterials =
        poMaterialsByMrqMaterialId.get(toKey(mmId)) ||
        poMaterialsByMrqMaterialId.get(toKey(mm.id)) ||
        [];
      mat.mrqMaterials = mat.mrqMaterials || [];
      mat.mrqMaterials.push({
        ...mm,
        mrq,
        poMaterials,
        poId: poMaterials[0]?.po?.id ?? (mm.poId != null ? mm.poId : undefined),
      });
    });
  });

  // MRVs: fetch all MRV details first (since /mrv list doesn't include plantId),
  // then filter by plantId from detailed responses, then attach mrv to materials
  const mrvDetails = await Promise.all(
    (mrvList || []).map((m) => getMrvById(m.id).catch(() => null))
  );
  
  // Process all MRVs - materials are already filtered by plantId, so if a material
  // is found in materialById, it belongs to this plant
  mrvDetails.forEach((mrv) => {
    if (!mrv) return;
    (mrv.mrvMrqMaterials || []).forEach((mrm) => {
      const mrqMaterialId = mrm.mrqMaterialId || mrm.mrqMaterial?.id;
      // mrqMaterial has a materials[] array, not a single material
      const materials = mrm.mrqMaterial?.materials || [];
      if (materials.length === 0) return;
      
      // Process each material in the materials array
      materials.forEach((material) => {
        const materialId = material?.id;
        if (!materialId) return;
        const mat = materialById.get(materialId);
        if (!mat) return; // Material not in this plant, skip
        
        mat.mrvMaterials = mat.mrvMaterials || [];
        if (!mat.mrvMaterials.some((m) => m.id === mrv.id)) {
          mat.mrvMaterials.push({ id: mrv.id, ...mrv });
        }
        
        // Link MRV to the corresponding mrqMaterial
        const mrqMat = (mat.mrqMaterials || []).find((mm) => mm.id === mrqMaterialId);
        if (mrqMat) {
          mrqMat.mrvMrqMaterials = mrqMat.mrvMrqMaterials || [];
          if (!mrqMat.mrvMrqMaterials.some((mrm) => mrm.mrv?.id === mrv.id)) {
            mrqMat.mrvMrqMaterials.push({ mrv });
          }
        }
      });
    });
    (mrv.mrvMaterials || []).forEach((mv) => {
      const materialId = mv.materialId || mv.material?.id;
      if (!materialId) return;
      const mat = materialById.get(materialId);
      if (!mat) return;
      mat.mrvMaterials = mat.mrvMaterials || [];
      if (!mat.mrvMaterials.some((m) => m.id === mrv.id)) {
        mat.mrvMaterials.push({ id: mrv.id, ...mrv });
      }
    });
  });

  // Merge material records (estCost, remainingQty) by mrqMaterialId if present
  const recordsByMrqMaterialId = new Map();
  const recordList = Array.isArray(materialRecordsPayload) ? materialRecordsPayload : [];
  recordList.forEach((rec) => {
    (rec.mrqMaterialsIds || [rec.mrqMaterialId]).filter(Boolean).forEach((id) => {
      recordsByMrqMaterialId.set(id, rec);
    });
  });

  // materialById may hold the same object under multiple keys; dedupe by reference
  const seen = new Set();
  const allMaterials = Array.from(materialById.values()).filter((mat) => {
    if (seen.has(mat)) return false;
    seen.add(mat);
    return true;
  });
  
  // Merge material records (estCost, remainingQty) by mrqMaterialId if present
  allMaterials.forEach((mat) => {
    (mat.mrqMaterials || []).forEach((mm) => {
      const rec = recordsByMrqMaterialId.get(mm.id);
      if (rec) {
        if (rec.estCost != null) mm.estCost = rec.estCost;
        if (rec.remainingQty != null) mm.remainingQty = rec.remainingQty;
      }
    });
    const firstMrqMat = mat.mrqMaterials?.[0];
    const rec = firstMrqMat && recordsByMrqMaterialId.get(firstMrqMat.id);
    if (rec && rec.estCost != null && mat.budgetedCost == null) {
      mat.budgetedCost = rec.estCost;
    }
  });

  // Consolidate materials by description (like MRQ consolidation)
  // Group materials with same description; use single quantity per line (first mrqMaterial's procuredQty)
  // so we don't triple-count when the same material appears on multiple MRQs
  const consolidatedMap = new Map();

  const getQuantityForMaterial = (mat) => {
    const first = mat.mrqMaterials?.[0];
    const fromMrq = first != null
      ? (Number(first.procuredQty) || Number(first.quantity) || 0)
      : 0;
    return fromMrq || Number(mat.quantity) || 0;
  };

  allMaterials.forEach((mat) => {
    const description = mat.description ||
                       (mat.taggedMaterial
                         ? `${mat.taggedMaterial?.component || ""} ${mat.taggedMaterial?.size || ""}`.trim()
                         : "-");

    if (!consolidatedMap.has(description)) {
      consolidatedMap.set(description, {
        ...mat,
        description,
        quantity: getQuantityForMaterial(mat),
        materials: [mat],
      });
    } else {
      const consolidated = consolidatedMap.get(description);
      consolidated.mrqMaterials = [
        ...(consolidated.mrqMaterials || []),
        ...(mat.mrqMaterials || []),
      ];
      consolidated.mrvMaterials = [
        ...(consolidated.mrvMaterials || []),
        ...(mat.mrvMaterials || []),
      ];
      // Keep quantity as first entry's quantity (don't sum - same material on multiple MRQs = one line)
      if (!consolidated.quantity || consolidated.quantity === 0) {
        consolidated.quantity = getQuantityForMaterial(mat);
      }
      consolidated.materials.push(mat);
    }
  });

  return Array.from(consolidatedMap.values());
}
